import { useEffect, useMemo, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useAuth } from '@/lib/auth';
import { fmtINR } from '@/lib/format';
import { Progress } from '@/components/ui/progress';
import { isOwner as _isOwner } from '@/lib/roles';
import {
  Target, TrendingUp, AlertTriangle, CheckCircle2, Circle, Info, MinusCircle, Users, Sparkles,
} from 'lucide-react';

const Sparkline = ({ data, direction = 'higher_is_better' }) => {
  if (!Array.isArray(data) || data.length < 2) return null;
  const valid = data.filter(v => v != null && Number.isFinite(v));
  if (valid.length < 2) return null;
  const min = Math.min(...valid);
  const max = Math.max(...valid);
  const range = max - min || 1;
  const w = 100, h = 24, pad = 2;
  const pts = data.map((v, i) => {
    const x = pad + (i * (w - pad * 2)) / (data.length - 1);
    if (v == null) return null;
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).filter(Boolean).join(' ');
  const last = valid[valid.length - 1];
  const first = valid[0];
  const delta = last - first;
  const goodDelta = direction === 'higher_is_better' ? delta > 0 : delta < 0;
  const color = delta === 0
    ? 'hsl(var(--foreground-subtle))'
    : goodDelta ? 'hsl(var(--success))' : 'hsl(var(--danger))';
  const lastX = pad + ((data.length - 1) * (w - pad * 2)) / (data.length - 1);
  const lastY = h - pad - ((last - min) / range) * (h - pad * 2);
  return (
    <div className="flex items-center gap-1.5 mt-2" data-testid="kra-sparkline">
      <svg viewBox={`0 0 ${w} ${h}`} width="100" height="24" className="overflow-visible">
        <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={lastX} cy={lastY} r="2" fill={color} />
      </svg>
      <span className="text-[10.5px] tabular-nums" style={{ color }}>
        {delta === 0 ? '·' : (delta > 0 ? '↑' : '↓')} 4w
      </span>
    </div>
  );
};

const STATUS_META = {
  on_track: { label: 'On track', chip: 'chip-success', icon: CheckCircle2, bar: 'bar-success' },
  at_risk: { label: 'At risk', chip: 'chip-warning', icon: AlertTriangle, bar: 'bar-warning' },
  off_track: { label: 'Off track', chip: 'chip-danger', icon: AlertTriangle, bar: 'bar-danger' },
  no_data: { label: 'No data', chip: '', icon: MinusCircle, bar: 'bar-neutral' },
  informational: { label: 'Info', chip: 'chip-info', icon: Info, bar: 'bar-info' },
};

const ROLE_LABEL = {
  owner: 'Owner', operations: 'Operations', dm_creative: 'DM — Creative',
  dm_technical: 'DM — Technical', accounts: 'Accounts', commercials: 'Commercials',
  hr: 'HR', team: 'Team',
};

function formatValue(k) {
  if (k.actual == null) return '—';
  if (k.unit === 'inr') return fmtINR(k.actual);
  if (k.unit === 'pct') return `${k.actual}%`;
  if (k.unit === 'days') return `${k.actual}d`;
  return String(k.actual);
}

function formatTarget(k) {
  if (k.target == null) return '—';
  if (k.unit === 'inr') return fmtINR(k.target);
  if (k.unit === 'pct') return `${k.target}%`;
  if (k.unit === 'days') return `${k.target}d`;
  return String(k.target);
}

function progressPct(k) {
  if (k.actual == null || k.target == null || k.target === 0) {
    if (k.direction === 'lower_is_better' && k.actual === 0 && k.target === 0) return 100;
    return null;
  }
  const ratio = k.actual / k.target;
  if (k.direction === 'higher_is_better') return Math.min(100, Math.round(ratio * 100));
  // lower_is_better: 0/target=100%, target/target=50%-ish visual — invert
  if (ratio <= 1) return 100;
  return Math.max(0, Math.round(100 - (ratio - 1) * 100));
}

export default function KraDashboardPage() {
  const { user } = useAuth();
  const isOwnerRole = _isOwner(user.role);
  const [tab, setTab] = useState('me');
  const [me, setMe] = useState(null);
  const [team, setTeam] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const r = await api.get('/kras/me_v3');
      setMe(r.data);
      if (isOwnerRole) {
        const t = await api.get('/kras/team');
        setTeam(t.data || []);
      }
    } catch (e) { toast.error(parseErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <AppShell breadcrumbs={[{ label: 'KRA Dashboard' }]}>
      <div className="p-6 max-w-[1300px] mx-auto fade-up" data-testid="kra-page">
        <div className="mb-4">
          <h1 className="font-heading text-2xl font-semibold flex items-center gap-2"><Target size={22} />KRA Dashboard</h1>
          <p className="text-[13px] text-[hsl(var(--foreground-muted))] mt-0.5">Clear line-of-sight into how you&rsquo;re being measured. Values compute live from your work in the platform.</p>
        </div>

        {isOwnerRole && (
          <Tabs value={tab} onValueChange={setTab} className="mb-3">
            <TabsList>
              <TabsTrigger value="me" data-testid="kra-tab-me"><Sparkles size={14} className="mr-1" />My KRAs</TabsTrigger>
              <TabsTrigger value="team" data-testid="kra-tab-team"><Users size={14} className="mr-1" />Team rollup</TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        {tab === 'me' && me && <KraGrid data={me} />}

        {tab === 'team' && (
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_2fr] gap-4">
            <div className="card p-2" data-testid="kra-team-list">
              <div className="max-h-[70vh] overflow-y-auto">
                {team.length === 0 && <div className="p-4 text-center text-[hsl(var(--foreground-muted))] text-[13px]">No team users found.</div>}
                {team.map((u) => {
                  const { on_track = 0, at_risk = 0, off_track = 0, no_data = 0 } = u.summary || {};
                  const total = on_track + at_risk + off_track + no_data + (u.summary.informational || 0);
                  const isSel = selected?.user_id === u.user_id;
                  return (
                    <button key={u.user_id} onClick={() => setSelected(u)}
                            data-testid={`kra-team-row-${u.user_id}`}
                            className={`w-full text-left px-3 py-2 rounded-md flex items-center justify-between hover:bg-[hsl(var(--surface-2))] ${isSel ? 'bg-[hsl(var(--surface-2))]' : ''}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-medium truncate">{u.name || u.email}</div>
                        <div className="text-[11.5px] text-[hsl(var(--foreground-muted))]">{ROLE_LABEL[u.role] || u.role}</div>
                      </div>
                      <div className="flex gap-1 items-center">
                        {off_track > 0 && <span className="chip chip-danger tabular-nums" title="Off track">{off_track}</span>}
                        {at_risk > 0 && <span className="chip chip-warning tabular-nums" title="At risk">{at_risk}</span>}
                        {on_track > 0 && <span className="chip chip-success tabular-nums" title="On track">{on_track}</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              {selected ? <KraGrid data={selected} viewingOther /> : (
                <div className="card p-6 text-center text-[13px] text-[hsl(var(--foreground-muted))]">Select a team member on the left to view their KRAs.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function KraGrid({ data, viewingOther = false }) {
  const summary = data.summary || {};
  const composite = data.composite_v3;
  const summaryChips = ['on_track', 'at_risk', 'off_track', 'informational', 'no_data']
    .filter(k => summary[k])
    .map(k => (
      <span key={k} className={`chip ${STATUS_META[k].chip}`} data-testid={`kra-summary-${k}`}>
        {STATUS_META[k].label}: {summary[k]}
      </span>
    ));
  const dispute = async () => {
    const reason = window.prompt('What looks off? (Owner + HR must respond within 7 days.)');
    if (!reason || reason.trim().length < 5) return;
    try {
      const r = await import('@/lib/api').then(m => m.api.post('/kras/dispute', { reason }));
      alert(`Question submitted. Response due by ${r.data.response_due_at?.slice(0,10)}.`);
    } catch (e) { alert('Could not submit — please try again.'); }
  };
  return (
    <div data-testid="kra-grid">
      {composite && (
        <div className="card p-4 mb-3" data-testid="kra-composite">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">Composite score (v3)</div>
              <div className="flex items-baseline gap-2 mt-1">
                <div className="font-heading text-3xl tabular-nums">{composite.composite ?? '—'}</div>
                <span className={`chip ${composite.status === 'green' ? 'chip-success' : composite.status === 'amber' ? 'chip-warning' : 'chip-danger'}`}>{composite.status}</span>
              </div>
            </div>
            <div className="flex gap-4 text-[12px]">
              {(composite.dimensions || []).map(dm => (
                <div key={dm.dimension} className="text-center min-w-[80px]" data-testid={`dim-${dm.dimension}`}>
                  <div className="text-[10.5px] uppercase text-[hsl(var(--foreground-subtle))] font-semibold">{dm.dimension}</div>
                  <div className="font-heading text-lg tabular-nums">{dm.score ?? '—'}</div>
                  <div className="text-[10.5px] text-[hsl(var(--foreground-muted))]">wt {dm.weight}% · {dm.events_count} events</div>
                </div>
              ))}
            </div>
            {!viewingOther && (
              <button onClick={dispute} className="chip chip-info" data-testid="kra-question-btn">Question this score</button>
            )}
          </div>
          <div className="text-[11px] text-[hsl(var(--foreground-subtle))] mt-2">
            This score is computed from the events listed on this page and nothing else. If something looks wrong, click <b>Question this score</b> — it routes to HR and Owner and must be answered in writing within 7 days.
          </div>
        </div>
      )}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {viewingOther && (
          <div className="font-heading text-lg font-semibold mr-2" data-testid="kra-view-name">
            {data.name || data.email} · <span className="text-[hsl(var(--foreground-muted))] font-normal text-[13px]">{ROLE_LABEL[data.role] || data.role}</span>
          </div>
        )}
        {summaryChips}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="kra-cards">
        {(data.kras || []).map(k => <KraCard key={k.key} k={k} />)}
      </div>
    </div>
  );
}

function KraCard({ k }) {
  const meta = STATUS_META[k.status] || STATUS_META.informational;
  const Icon = meta.icon;
  const pct = progressPct(k);
  const contextText = useMemo(() => {
    const c = k.context || {};
    const pieces = [];
    if (c.won != null && c.lost != null) pieces.push(`${c.won} won · ${c.lost} lost`);
    if (c.within != null && c.total != null) pieces.push(`${c.within} within / ${c.total} total`);
    if (c.approved != null && c.total != null) pieces.push(`${c.approved} approved / ${c.total} reviewed`);
    if (c.set != null && c.active != null) pieces.push(`${c.set} of ${c.active} projects`);
    if (c.read != null && c.total != null) pieces.push(`${c.read} of ${c.total} read`);
    if (c.pending != null && c.inactive != null) pieces.push(`${c.pending} pending · ${c.inactive} inactive`);
    if (c.projects != null && Array.isArray(c.projects) && c.projects.length) pieces.push(c.projects.slice(0, 2).join(', ') + (c.projects.length > 2 ? ` +${c.projects.length - 2} more` : ''));
    if (c.milestones != null) pieces.push(`${c.milestones} milestones`);
    if (c.visits != null) pieces.push(`${c.visits} visits`);
    if (c.sample_size != null) pieces.push(`n=${c.sample_size}`);
    return pieces.join(' · ');
  }, [k.context]);

  return (
    <div className="card p-4" data-testid={`kra-card-${k.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[11.5px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">{k.label}</div>
          {k.description && <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] mt-0.5 line-clamp-2">{k.description}</div>}
        </div>
        <span className={`chip ${meta.chip} inline-flex items-center gap-1 flex-none`}>
          <Icon size={11} />{meta.label}
        </span>
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <div className="font-heading text-2xl tabular-nums">{formatValue(k)}</div>
        {k.target != null && (
          <div className="text-[12px] text-[hsl(var(--foreground-muted))]">
            {k.direction === 'higher_is_better' ? '≥' : '≤'} <span className="tabular-nums">{formatTarget(k)}</span>
          </div>
        )}
      </div>
      {pct != null && (
        <div className="mt-3">
          <Progress value={pct} className="h-1.5" />
        </div>
      )}
      {Array.isArray(k.spark) && <Sparkline data={k.spark} direction={k.direction} />}
      {contextText && (
        <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] mt-2 truncate" title={contextText}>{contextText}</div>
      )}
    </div>
  );
}
